import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, Users } from "lucide-react";
import { formatByCurrency } from "../../../utils/formatCurrency";
import TablePagination, { PAGE_SIZE } from "./TablePagination";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const formatPriceRange = (min, max, currency) => {
  if (min == null && max == null) return "—";
  if (min != null && max != null && min !== max) {
    return `${formatByCurrency(min, currency)} - ${formatByCurrency(max, currency)}`;
  }
  return formatByCurrency(min ?? max, currency);
};


export default function CompetitorActivityPanel({ competitors = [], loading = false }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const rows = useMemo(() => {
    const flat = [];
    competitors.forEach((competitor) => {
      (competitor.outlets || []).forEach((outlet) => {
        flat.push({
          competitor_id: competitor.competitor_id,
          competitor_name: competitor.competitor_name,
          outlet_id: outlet.outlet_id,
          outlet_name: outlet.outlet_name,
          proofs: outlet.proofs,
          last_seen: outlet.last_seen,
          note: (outlet.notes || []).filter(Boolean).join(" · "),
          price_range_min: outlet.price_range_min,
          price_range_max: outlet.price_range_max,
          price_range_currency: outlet.price_range_currency,
        });
      });
    });
    return flat;
  }, [competitors]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter(
      (row) =>
        (row.competitor_name || "").toLowerCase().includes(query) ||
        (row.note || "").toLowerCase().includes(query),
    );
  }, [rows, search]);

  // Jump back to page 1 whenever the result set changes underneath us — either
  // the user typed in the search box, or the parent refetched after an outlet /
  // date filter change. Without this, staying on page 4 of a now-2-page result
  // would show an empty table.
  useEffect(() => {
    setPage(1);
  }, [search, competitors]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);

  // Group markers are computed after slicing so every page labels its own
  // groups — otherwise a page starting mid-group shows a nameless competitor.
  const display = useMemo(() => {
    const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
    let previous = null;
    return pageRows.map((row) => {
      const firstOfGroup = row.competitor_id !== previous;
      previous = row.competitor_id;
      return { ...row, firstOfGroup };
    });
  }, [filtered, safePage]);

  const competitorCount = useMemo(
    () => new Set(filtered.map((row) => row.competitor_id)).size,
    [filtered],
  );

  if (loading) {
    return <div className="dpi-chart-empty">{t("admin.insights.competitorActivity.loading")}</div>;
  }
  if (!rows.length) {
    return <div className="dpi-chart-empty">{t("admin.insights.competitorActivity.empty")}</div>;
  }

  return (
    <div className="dpi-op dpi-op--comp">
      <div className="dpi-op-toolbar">
        <div className="dpi-op-search">
          <Search size={15} />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("admin.insights.competitorActivity.searchPlaceholder")}
          />
        </div>
        <span className="dpi-op-count">
          {t("admin.insights.competitorActivity.tableSummary", {
            competitors: competitorCount,
            outlets: filtered.length,
          })}
        </span>
      </div>

      <div className="dpi-op-scroll">
        <table className="dpi-op-table">
          <thead>
            <tr>
              <th className="dpi-op-th-outlet">{t("admin.insights.competitorActivity.competitor")}</th>
              <th>{t("admin.insights.competitorActivity.outlet")}</th>
              <th>{t("admin.insights.competitorActivity.observation")}</th>
              <th>{t("admin.insights.competitorActivity.priceRange")}</th>
              <th className="dpi-num">{t("admin.insights.competitorActivity.proofs")}</th>
              <th>{t("admin.insights.competitorActivity.lastSeen")}</th>
            </tr>
          </thead>
          <tbody>
            {display.length === 0 ? (
              <tr>
                <td colSpan={6} className="dpi-op-nomatch">
                  {t("admin.insights.competitorActivity.noMatch")}
                </td>
              </tr>
            ) : (
              display.map((row) => (
                <tr
                  key={`${row.competitor_id}-${row.outlet_id}`}
                  className={row.firstOfGroup ? "dpi-op-row dpi-op-row--group" : "dpi-op-row"}
                >
                  <td className="dpi-op-outlet">
                    {row.firstOfGroup ? (
                      <span className="dpi-op-outlet-name" title={row.competitor_name}>
                        <Users size={13} />
                        {row.competitor_name}
                      </span>
                    ) : null}
                  </td>
                  <td className="dpi-op-product">{row.outlet_name}</td>
                  <td className="dpi-op-note">
                    {row.note ? row.note : <span className="dpi-muted">—</span>}
                  </td>
                  <td className="dpi-muted">
                    {formatPriceRange(
                      row.price_range_min,
                      row.price_range_max,
                      row.price_range_currency,
                    )}
                  </td>
                  <td className="dpi-num">{row.proofs}</td>
                  <td className="dpi-muted">{formatDate(row.last_seen)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <TablePagination
        page={safePage}
        pageCount={pageCount}
        totalRows={filtered.length}
        onPageChange={setPage}
      />
    </div>
  );
}
